import { ArrowRight, MapPin, Sparkle } from 'lucide-react';
import { Reveal, Eyebrow } from './Reveal';
import { AREAS, scrollToId } from '../config/site';

const SEPARATOR_COLORS = ['text-aqua', 'text-fresh', 'text-aqua'];

const Marquee = () => (
  <div
    className="mt-16 overflow-hidden border-y border-navy/10 bg-white py-7 lg:mt-20"
    aria-label={`Service areas: ${AREAS.join(', ')}`}
  >
    <div className="ks-marquee-track">
      {[0, 1].map((copy) => (
        <ul key={copy} aria-hidden={copy === 1} className="flex shrink-0 items-center">
          {AREAS.map((area, i) => (
            <li key={`${copy}-${area}`} className="flex items-center whitespace-nowrap">
              <span className="flex items-center gap-3 px-10 sm:px-14">
                <MapPin size={16} className="text-fresh" aria-hidden="true" />
                <span className="text-xl font-extrabold tracking-wide text-navy sm:text-2xl">
                  {area}
                </span>
              </span>
              <Sparkle
                size={13}
                className={SEPARATOR_COLORS[i % SEPARATOR_COLORS.length]}
                fill="currentColor"
                aria-hidden="true"
              />
            </li>
          ))}
        </ul>
      ))}
    </div>
  </div>
);

export const ServiceAreas = () => (
  <section id="service-areas" className="ks-areas relative scroll-mt-24 overflow-hidden py-20 lg:py-28">
    <svg
      viewBox="0 0 1440 70"
      preserveAspectRatio="none"
      className="absolute inset-x-0 top-0 h-[46px] w-full"
      aria-hidden="true"
    >
      <path d="M0,0 L1440,0 L1440,10 C1100,58 420,60 0,16 Z" fill="#09254e" />
    </svg>

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid items-end gap-12 lg:grid-cols-2 lg:gap-24">
        <Reveal>
          <Eyebrow>Service Areas</Eyebrow>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Residential Cleaning Across Central Florida
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-charcoal sm:text-lg">
            Proudly serving homes across Central Florida, including the communities below and
            surrounding areas.
          </p>
          <p className="mt-4 max-w-lg rounded-2xl border-l-4 border-fresh bg-white/70 px-5 py-4 text-sm font-semibold leading-relaxed text-navy">
            Don't see your area listed? Request a free quote — we may still be able to serve your
            home.
          </p>
          <button
            type="button"
            data-testid="areas-quote-button"
            onClick={() => scrollToId('quote')}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-electric px-9 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-navy"
          >
            Get a Free Quote
            <ArrowRight size={16} />
          </button>
        </Reveal>

        <Reveal delay={0.1}>
          <ul className="grid grid-cols-1 gap-x-10 sm:grid-cols-2" data-testid="areas-list">
            {AREAS.map((a) => (
              <li
                key={a}
                className="flex items-center gap-4 border-t border-navy/10 py-4 last:border-b sm:py-5 [&:nth-child(5)]:sm:border-b"
                data-testid={`area-${a.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <MapPin size={17} className="shrink-0 text-fresh" aria-hidden="true" />
                <span className="text-lg font-bold tracking-tight text-navy sm:text-xl">{a}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm leading-relaxed text-charcoal/80">
            These are the communities we currently focus on — and we often serve nearby areas too.
          </p>
        </Reveal>
      </div>
    </div>

    <Reveal delay={0.15}>
      <Marquee />
    </Reveal>
  </section>
);

export default ServiceAreas;
